import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-leaflet';
import { LatLngTuple } from 'leaflet';
import styled from 'styled-components';
import { getContentType } from '../common/Common';
import { getTranslation } from '../common/Translation';
import { AppContext } from '../contexts/AppContext';
import ImageMapPopup from './ImageMapPopup';
import { MAP_CIRCLE_ANCHOR, MAP_CIRCLE_SIZE, MAP_PIN_ANCHOR, MAP_PIN_SIZE } from './MapPin';
import useFetchDocument from '../hooks/useFetchDocument';
import useFetchMedia from '../hooks/useFetchMedia';

interface MapMarkerPopupProps {
  documentId: number,
  latLng: LatLngTuple,
  title?: string,
  subtitle?: string,
  image?: any,
  markerColor?: string,
  markerStyle?: string,
  onClose?: () => void,
  /** When true, the popup is shown but does not link to the presentation */
  notClickable?: boolean
}

const PopupAnchor = styled.div<{
  $x: number,
  $y: number,
  $width: number,
  $height: number,
  $interactive: boolean
}>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  z-index: 1200;
  pointer-events: none;

  & .imagemappopup {
    pointer-events: ${({ $interactive }) => ($interactive ? "all" : "none")};
  }
`;

/**
 * Resolve icon top-left and size from the Leaflet latlng anchor point.
 */
const getIconBox = (
  containerPoint: { x: number, y: number },
  markerStyle?: string
) => {
  const isCircle = markerStyle === "circle";
  const size = isCircle ? MAP_CIRCLE_SIZE : MAP_PIN_SIZE;
  const anchor = isCircle ? MAP_CIRCLE_ANCHOR : MAP_PIN_ANCHOR;
  return {
    x: containerPoint.x - anchor.x,
    y: containerPoint.y - anchor.y,
    width: size.width,
    height: size.height
  };
};

/**
 * Gap past the icon box edge + vertical caret target (same for left and right).
 * With a sized anchor, left/right offsets are symmetric — unlike a 0×0 origin.
 */
const getPopupOffset = (markerStyle?: string) => {
  if (markerStyle === "circle") {
    return {
      x: MAP_CIRCLE_SIZE.width + 14,
      y: MAP_CIRCLE_ANCHOR.y
    };
  }

  return {
    x: MAP_PIN_SIZE.width + 18,
    y: 20
  };
};

/**
 * Whether a media ref is expected to resolve to a displayable image.
 */
const expectsMedia = (media: any) =>
  Boolean(media?.mediaId || media?.url || media?.dmsId);

/**
 * Positioned ImageMap popup for a selected map marker.
 * Uses an icon-sized anchor so left/right placement keeps equal gap to the marker.
 * @returns {JSX.Element | null} Component template
 */
const MapMarkerPopup: FC<MapMarkerPopupProps> = ({
  documentId,
  latLng,
  title,
  subtitle,
  image,
  markerColor,
  markerStyle,
  onClose,
  notClickable
}) => {
  const map = useMap();
  const { currentLanguage } = useContext(AppContext);
  const popupOffset = useMemo(() => getPopupOffset(markerStyle), [markerStyle]);

  // Lock side once at open
  const [placement] = useState(() => {
    const size = map.getSize();
    const markerPoint = map.latLngToContainerPoint(latLng);
    return {
      left: markerPoint.x > size.x * 0.55,
      bottom: markerPoint.y > size.y * 0.65
    };
  });

  const [box, setBox] = useState(() => {
    const containerPoint = map.latLngToContainerPoint(latLng);
    return getIconBox(containerPoint, markerStyle);
  });
  const [interactive, setInteractive] = useState(false);
  const [data] = useFetchDocument(documentId);

  const mediaSource = image?.mediaId
    ? image
    : (data?.content?.general?.image || data?.content?.background?.image);
  const waitingForMedia = expectsMedia(mediaSource);
  const [presentationMedia] = useFetchMedia(mediaSource, { dimension: "600x600" });
  const [mediaWaitExpired, setMediaWaitExpired] = useState(false);

  const presentationTitle = getTranslation(title, currentLanguage)?.length > 0
    ? getTranslation(title, currentLanguage)
    : getTranslation(data?.title, currentLanguage);
  const presentationSubtitle = getTranslation(subtitle, currentLanguage);
  const contentType = getContentType(data) || "article";

  useEffect(() => {
    setMediaWaitExpired(false);
    if (!waitingForMedia || presentationMedia?.url) { return; }
    const timer = window.setTimeout(() => setMediaWaitExpired(true), 2500);
    return () => window.clearTimeout(timer);
  }, [waitingForMedia, presentationMedia?.url, documentId]);

  const contentReady = Boolean(data) && (
    !waitingForMedia || Boolean(presentationMedia?.url) || mediaWaitExpired
  );

  useEffect(() => {
    const update = () => {
      const containerPoint = map.latLngToContainerPoint(latLng);
      setBox(getIconBox(containerPoint, markerStyle));
    };

    update();
    map.on("move", update);
    map.on("zoom", update);
    map.on("zoomend", update);
    map.on("moveend", update);

    return () => {
      map.off("move", update);
      map.off("zoom", update);
      map.off("zoomend", update);
      map.off("moveend", update);
    };
  }, [map, latLng, markerStyle]);

  useEffect(() => {
    if (!contentReady) { return; }
    setInteractive(false);
    const timer = window.setTimeout(() => setInteractive(true), 400);
    return () => window.clearTimeout(timer);
  }, [contentReady, documentId]);

  if (!contentReady) {
    return null;
  }

  return (
    <PopupAnchor
      $x={box.x}
      $y={box.y}
      $width={box.width}
      $height={box.height}
      $interactive={interactive}
      onClick={(e) => e.stopPropagation()}
    >
      <ImageMapPopup
        documentId={documentId}
        title={presentationTitle}
        subtitle={presentationSubtitle}
        image={presentationMedia}
        position={{ x: box.x, y: box.y }}
        contentType={contentType}
        markerColor={markerColor}
        onClick={onClose}
        defaultVisible
        placement={placement}
        offset={popupOffset}
        notClickable={notClickable}
      />
    </PopupAnchor>
  );
};

export default MapMarkerPopup;
